import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  setFilterId,
  setForecastMode,
  setGlobalGroupBy,
  setSyncWells,
  setUseForecastStartDate,
  setUserForecastSettings
} from "store/features";
import { setFilterId as setFacilityFilterId } from "store/features/filter/facilityFilterSlice";
import {
  setGlobalNormalizeBy,
  setUseNormalizeBy
} from "store/features/normalizeBy/normalizeBySlice";
import {
  setActiveColorPalette,
  setActiveFacilityColorPalette,
  setColorPalettes
} from "store/features/userSettings/userSettingsSlice";
import { useForecastStore } from "stores/forecast";
import { useMapStore } from "stores/map";
import type { PopoutChannelState } from "types/popout";

import { EntityKind } from "models/entityKind";

import { useUserContext } from "components/user/context";
import { updateSession } from "components/user/context/reducer/UserReducer";

/**
 * Restores the per-channel state captured at pop-out time (written to
 * sessionStorage by the Popout toggle) into the popout tab's stores on first
 * render.
 *
 * Extracted from PopoutChartPane so the restore contract can be tested in
 * isolation without rendering the full (provider-heavy) popout view — see
 * usePopoutInitialState.test.tsx. EVA-6317.
 */
export function usePopoutInitialState(
  entityKind: EntityKind,
  // sessionStorage may predate a field (older deploy) or be absent, so the
  // restored payload is treated as a partial of the writer's contract.
  initialState: Partial<PopoutChannelState> | null
): void {
  const dispatch = useDispatch();
  const [, userDispatch] = useUserContext();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized) {
      return;
    }
    if (initialState?.filterId) {
      dispatch(
        entityKind == EntityKind.Well
          ? setFilterId(initialState.filterId)
          : setFacilityFilterId(initialState.filterId)
      );
    }

    if (initialState?.checkedGlobalTypeWells && entityKind == EntityKind.Well) {
      useForecastStore.setState({
        checkedGlobalTypeWells: initialState.checkedGlobalTypeWells
      });
    }

    if (initialState?.checkedGlobalForecasts && entityKind == EntityKind.Well) {
      useForecastStore.setState({
        checkedGlobalForecasts: initialState.checkedGlobalForecasts
      });
    }

    if (initialState?.globalGroupBy && entityKind == EntityKind.Well) {
      dispatch(setGlobalGroupBy(initialState.globalGroupBy));
    }

    if (initialState?.sessionId) {
      updateSession(userDispatch, initialState.sessionId);
    }

    if (initialState?.activeColorPalette) {
      if (entityKind === EntityKind.Well) {
        dispatch(setActiveColorPalette(initialState.activeColorPalette));
      } else {
        dispatch(setActiveFacilityColorPalette(initialState.activeColorPalette));
      }
    }

    if (initialState?.globalNormalizeBy) {
      dispatch(setGlobalNormalizeBy(initialState.globalNormalizeBy));
    }

    if (initialState?.useNormalizeBy) {
      dispatch(setUseNormalizeBy(initialState.useNormalizeBy));
    }

    if (initialState?.colorPalettes) {
      dispatch(setColorPalettes(initialState.colorPalettes));
    }

    if (initialState?.syncWells) {
      dispatch(setSyncWells(initialState.syncWells));
    }

    // EVA-6317: forecast compare mode, "show from start date", and the active forecast
    // folder selection live in state.app and weren't transferred to the popout, so the new
    // tab booted in Trump mode with no source folder until the user toggled something in the
    // main tab (which fired an action that redux-state-sync then propagated).
    if (initialState?.forecastMode) {
      dispatch(setForecastMode(initialState.forecastMode));
    }

    if (initialState?.useForecastStartDate !== undefined) {
      dispatch(setUseForecastStartDate(initialState.useForecastStartDate));
    }

    if (initialState?.userForecastSettings) {
      dispatch(setUserForecastSettings(initialState.userForecastSettings));
    }

    // EVA-124: the chart legend sources its items and title from the map store's
    // txnId, which only the map writes. Until the State Management migration
    // (#5871) this arrived via redux-state-sync replaying map/setTxnId; now it is a
    // Zustand field, so restore it from the snapshot like StandaloneDashboard does.
    if (initialState?.txnId) {
      useMapStore.getState().setTxnId(initialState.txnId);
    }

    // Unconditional: PopoutChartPane re-parses `initialState` every render, so
    // this effect would otherwise re-run until it hit a snapshot with
    // globalGroupBy. txnId is also live-synced after mount, and a re-applied
    // snapshot would fight useTxnIdCrossTabSubscriber.
    setIsInitialized(true);
  }, [entityKind, dispatch, initialState, isInitialized, userDispatch]);
}
